/**
 * InputBar component
 *
 * Text input for sending messages with send button and share menu.
 */

import { useState, useCallback, useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ShareMenu } from './ShareMenu';

interface InputBarProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  sessionUuid?: string;
  hasMessages?: boolean;
}

export function InputBar({
  onSend,
  disabled = false,
  placeholder,
  sessionUuid,
  hasMessages = false,
}: InputBarProps) {
  const { t } = useTranslation();
  const finalPlaceholder = placeholder || t('chat.placeholder');
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed && !disabled) {
      onSend(trimmed);
      setValue('');
      // Re-focus input after send
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [value, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  return (
    <div className="input-area">
      <textarea
        ref={textareaRef}
        data-testid="chat-input"
        id="input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={finalPlaceholder}
        disabled={disabled}
        rows={1}
      />
      <div className="input-actions">
        {sessionUuid && hasMessages && (
          <ShareMenu sessionUuid={sessionUuid} disabled={disabled} />
        )}
        <button
          data-testid="send-button"
          className="send-btn"
          onClick={handleSend}
          disabled={disabled || !value.trim()}
        >
          ➤
        </button>
      </div>
    </div>
  );
}

export default InputBar;
